/**
 * ContratsBulkImport — Import massif de contrats via ZIP.
 * Le user drop un ZIP → l'IA analyse chaque PDF en background.
 * Progression polling toutes les 3s.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Upload, Loader2, FileArchive, CheckCircle2, XCircle, ArrowRight,
  Sparkles, PlayCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  running: { label: "En cours", cls: "bg-blue-100 text-blue-700 border-blue-300" },
  completed: { label: "Terminé", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
};

export default function ContratsBulkImport({ embedded = false } = {}) {
  const navigate = useNavigate();
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const inputRef = useRef();

  const loadJobs = async () => {
    try {
      const r = await api.get("/contracts/bulk-jobs");
      setJobs(r.data.jobs || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadJobs(); }, []);

  // Polling on active job
  useEffect(() => {
    if (!currentJob || currentJob.status === "completed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/contracts/bulk-jobs/${currentJob.id}`);
        setCurrentJob(r.data.job);
        if (r.data.job.status === "completed") { loadJobs(); }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [currentJob]);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Fichier ZIP requis"); return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("ZIP > 200 MB"); return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/contracts/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      toast.success(`Import lancé : ${data.total} contrats en cours d'analyse`);
      // Load full job detail
      const r = await api.get(`/contracts/bulk-jobs/${data.job_id}`);
      setCurrentJob(r.data.job);
      loadJobs();
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            Import massif
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Drop un ZIP de contrats — l'IA analyse chaque PDF en background,
            détecte les risques, embed pour la recherche sémantique. Idéal
            pour reprendre l'historique d'une entreprise (jusqu'à 100 contrats).
          </p>
        </div>
      </div>

      {/* Dropzone */}
      {!currentJob && (
        <Card>
          <div
            onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className={cn(
              "cursor-pointer rounded-xl border-2 border-dashed p-16 text-center transition-all",
              drag ? "border-blue-500 bg-blue-50"
                    : "border-slate-300 hover:border-blue-400 hover:bg-slate-50",
              uploading && "pointer-events-none opacity-60"
            )}
            data-testid="bulk-dropzone"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              {uploading
                ? <Loader2 className="h-7 w-7 text-blue-600 animate-spin" />
                : <FileArchive className="h-7 w-7 text-blue-600" />}
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {uploading ? "Téléversement en cours…" : "Dépose un ZIP de contrats"}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Ou clique pour parcourir · ZIP contenant PDF/JPG/PNG · 200 MB max · 100 contrats max
            </p>
            <div className="inline-flex items-center gap-2 text-xs text-slate-400">
              <Sparkles className="h-3 w-3" /> Chaque PDF ≈ 20 s d'analyse IA · résultats en background
            </div>
            <input id="bulk-file-input" name="bulk-file-input"
                    type="file" ref={inputRef} className="hidden" accept=".zip"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="bulk-file-input" />
          </div>
        </Card>
      )}

      {/* Active job progress */}
      {currentJob && (
        <Card className="p-5" data-testid="bulk-job-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge className={cn(STATUS_META[currentJob.status]?.cls)}>
                {currentJob.status === "running" && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                {STATUS_META[currentJob.status]?.label}
              </Badge>
              <span className="text-sm font-medium">
                {currentJob.done + currentJob.failed} / {currentJob.total} traités
              </span>
              {currentJob.failed > 0 && (
                <Badge className="bg-red-100 text-red-700 border-red-300">
                  {currentJob.failed} échec(s)
                </Badge>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setCurrentJob(null)}>
              Fermer
            </Button>
          </div>
          {currentJob.current_file && currentJob.status === "running" && (
            <div className="text-xs text-slate-500 mb-3 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" />
              Traitement en cours : <b className="text-slate-700">{currentJob.current_file}</b>
            </div>
          )}
          <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
                  style={{ width: `${Math.round(100 * (currentJob.done + currentJob.failed) / currentJob.total)}%` }} />
          </div>

          <div className="space-y-1 max-h-[420px] overflow-y-auto">
            {(currentJob.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-xs p-2 rounded border border-slate-100 bg-white"
                   data-testid={`bulk-item-${i}`}>
                {it.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                {it.status === "failed" && <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />}
                {it.status === "processing" && <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin shrink-0" />}
                {it.status === "queued" && <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
                <span className="flex-1 truncate text-slate-700">{it.filename}</span>
                {it.status === "done" && it.contract_id && (
                  <>
                    <Badge className={cn("text-[9px]",
                      (it.risk_score || 0) >= 51 ? "bg-red-100 text-red-800 border-red-300"
                      : (it.risk_score || 0) >= 21 ? "bg-amber-100 text-amber-800 border-amber-300"
                      : "bg-emerald-100 text-emerald-800 border-emerald-300")}>
                      Risque {it.risk_score || 0}
                    </Badge>
                    <button onClick={() => navigate(`/app/contracts/${it.contract_id}`)}
                            className="text-blue-600 hover:underline">
                      Voir <ArrowRight className="h-3 w-3 inline" />
                    </button>
                  </>
                )}
                {it.status === "failed" && it.error && (
                  <span className="text-red-600 text-[10px] truncate max-w-xs" title={it.error}>{it.error}</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Historique */}
      {jobs.length > 0 && !currentJob && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold mb-3">Historique des imports</h2>
          <div className="space-y-1.5">
            {jobs.slice(0, 20).map((j) => (
              <button key={j.id} onClick={async () => {
                const r = await api.get(`/contracts/bulk-jobs/${j.id}`);
                setCurrentJob(r.data.job);
              }}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 text-left transition"
                data-testid={`job-history-${j.id}`}>
                <PlayCircle className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    Import du {new Date(j.created_at).toLocaleString("fr-FR")}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {j.done}/{j.total} OK · {j.failed} échecs · par {j.created_by_email}
                  </div>
                </div>
                <Badge className={cn(STATUS_META[j.status]?.cls)}>
                  {STATUS_META[j.status]?.label}
                </Badge>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
